import { useCallback, useMemo, useRef } from 'react'
import { SearchItemLabel, usePopup } from '..'
import { intl } from '../../../../../i18n'
import { useProjectPrivateContext } from '../../../../context/projectContext'
import { WbsItemType } from '../../../../../domain/entity/WbsItemEntity'
import ProcessScopeSearchSelectPopper from '../../../../features/ProcessScopeSearchSelectPopper/ProcessScopeSearchSelectPopper'
import { useProcessScopeOptions } from '../../../../features/ProcessScopeSearchSelectPopper/useProcessScopeOptions'
import { isDescendantOf } from '../../../../features/ProcessScopeSearchSelectPopper/treeUtils'

const PROCESS_SCOPE_WBS_ITEM_TYPES = [
  WbsItemType.WORKGROUP,
  WbsItemType.PROCESS,
  WbsItemType.DELIVERABLE_LIST,
]

type ProcessScopeConditionItemLabelProps = {
  value: string[] | undefined
  onChange: (newValue: string[] | undefined) => void
  onClose: () => void
  search: () => void
  isLoading: boolean
}

export const ProcessScopeConditionItemLabel = ({
  value,
  onChange,
  onClose,
  search,
  isLoading,
}: ProcessScopeConditionItemLabelProps) => {
  const { ref, open, openPopup, closePopup } = usePopup()
  const { projectUuid } = useProjectPrivateContext()
  const { options, parentMap } = useProcessScopeOptions(
    projectUuid,
    PROCESS_SCOPE_WBS_ITEM_TYPES
  )

  const selectedValues = useMemo(() => new Set(value || []), [value])

  const valueNames = useMemo(() => {
    return options
      .filter(op => (value || []).includes(op.value))
      .map(op => op.name)
  }, [value, options])

  const onClear = useCallback(() => {
    onChange(undefined)
    onClose()
  }, [onChange, onClose])

  const onClosePopup = useCallback(() => {
    search()
    closePopup()
  }, [search, closePopup])

  const valueRef = useRef(value)
  valueRef.current = value

  const onChangeRef = useRef(onChange)
  onChangeRef.current = onChange

  const handleToggleItem = useCallback(
    (itemValue: string) => {
      const current = new Set(valueRef.current || [])
      if (current.has(itemValue)) {
        current.delete(itemValue)
      } else {
        current.add(itemValue)
        // 親を選択した場合、配下の選択を解除する
        for (const selected of [...current]) {
          if (
            selected !== itemValue &&
            isDescendantOf(selected, itemValue, parentMap)
          ) {
            current.delete(selected)
          }
        }
      }
      const newValue = Array.from(current)
      const result = newValue.length > 0 ? newValue : undefined
      onChangeRef.current(result)
      if (newValue.length === 0) {
        search()
        closePopup()
        onClose()
      }
    },
    [parentMap, search, closePopup, onClose]
  )

  if (valueNames.length === 0) return <></>
  return (
    <>
      <SearchItemLabel
        ref={ref}
        active={open}
        onClick={openPopup}
        onClear={onClear}
        isLoading={isLoading}
      >
        {intl.formatMessage({ id: 'wbsItemSearchFilterPanel.processScope' })}:{' '}
        {valueNames.join(', ')}
      </SearchItemLabel>
      {open && !isLoading && (
        <ProcessScopeSearchSelectPopper
          open={open && !isLoading}
          anchorEl={ref.current || undefined}
          options={options}
          parentMap={parentMap}
          isLoading={false}
          selectedValues={selectedValues}
          onToggleItem={handleToggleItem}
          onClose={onClosePopup}
          disablePortal={false}
        />
      )}
    </>
  )
}
